// Aramali nokta secici — combobox stili (filtre input, listede gez, tikla sec).

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, MapPin, X } from 'lucide-react';
import { noktaGetir } from '@emanetx/sdk';
import { istemci } from '../istemci.js';
import { useNoktaListesi } from './api.js';

export function NoktaSecici({
  seciliId,
  onSec,
  placeholder = 'Tüm noktalar',
  className,
  temizlenebilir = true,
}: {
  seciliId: string;
  onSec: (id: string) => void;
  placeholder?: string;
  className?: string;
  temizlenebilir?: boolean;
}) {
  const [acik, setAcik] = useState(false);
  const [arama, setArama] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!acik) return;
    const tik = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setAcik(false);
      }
    };
    document.addEventListener('mousedown', tik);
    return () => document.removeEventListener('mousedown', tik);
  }, [acik]);

  // Tum noktalar — server-side arama (boyut max 100, backend siniri)
  const liste = useNoktaListesi({
    sayfa: 1,
    boyut: 100,
    ...(arama && { arama }),
  });

  const noktalar = liste.data?.veriler ?? [];

  // Secili nokta listede degilse (paginasyon sinirini astiysa) tek tek getir
  const seciliListede = noktalar.find((n) => n.id === seciliId);
  const seciliDetay = useQuery({
    queryKey: ['nokta-secici-fallback', seciliId],
    queryFn: () => noktaGetir(istemci, seciliId),
    enabled: !!seciliId && !seciliListede && !liste.isLoading,
    staleTime: 60_000,
  });

  const seciliGoster = seciliListede ?? seciliDetay.data ?? null;
  const goster = seciliGoster ? `${seciliGoster.ad} (${seciliGoster.kod})` : placeholder;

  return (
    <div ref={ref} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setAcik(!acik)}
        className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 hover:border-marka-300 flex h-10 w-full min-w-[200px] items-center justify-between gap-2 rounded-lg border bg-white px-3 text-xs transition-colors"
      >
        <span
          className={`flex min-w-0 items-center gap-1.5 truncate ${
            seciliGoster ? '' : 'text-fume-400'
          }`}
        >
          <MapPin size={12} className="text-fume-400 flex-shrink-0" />
          <span className="truncate">{goster}</span>
        </span>
        <div className="flex flex-shrink-0 items-center gap-1">
          {temizlenebilir && seciliId && (
            <span
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                onSec('');
              }}
              className="text-fume-400 cursor-pointer hover:text-red-600"
              aria-label="Temizle"
            >
              <X size={12} />
            </span>
          )}
          <ChevronsUpDown size={12} className="text-fume-400" />
        </div>
      </button>

      {acik && (
        <div className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 absolute z-50 mt-1 max-h-72 w-full min-w-[280px] overflow-hidden rounded-lg border bg-white shadow-2xl">
          <input
            type="text"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
            placeholder="Ara: ad, kod, adres…"
            autoFocus
            className="border-fume-100 dark:border-fume-800 dark:bg-fume-900 dark:text-fume-50 w-full border-b bg-white px-3 py-2 text-xs focus:outline-none"
          />
          <ul className="max-h-56 overflow-y-auto py-1">
            {liste.isLoading ? (
              <li className="text-fume-500 px-3 py-2 text-xs">Yükleniyor…</li>
            ) : noktalar.length === 0 ? (
              <li className="text-fume-500 px-3 py-2 text-xs">
                {arama ? 'Eşleşen yok' : 'Kayıtlı nokta yok'}
              </li>
            ) : (
              noktalar.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSec(n.id);
                      setAcik(false);
                      setArama('');
                    }}
                    className={`hover:bg-fume-100 dark:hover:bg-fume-800 flex w-full items-center gap-2 px-3 py-2 text-left text-xs ${
                      n.id === seciliId ? 'bg-marka-50 dark:bg-marka-500/10' : ''
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-fume-900 dark:text-fume-50 truncate font-medium">
                        {n.ad}
                      </div>
                      <div className="text-fume-500 truncate font-mono text-[10px]">
                        {n.kod}
                        {n.adres && ` · ${n.adres}`}
                      </div>
                    </div>
                    {n.id === seciliId && (
                      <Check size={13} className="text-marka-600 flex-shrink-0" />
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
